import axios, { AxiosError } from 'axios';
import { sendError } from '@pec/aion-ui-core/sendError';
import { ThunkAction } from 'redux-thunk';
import { RootState } from '../combineReducers';
import {
  FetchPaymentMethodsParams,
  FetchPaymentMethodsPayload,
  VerifySufficientFundsParams,
  VerifySufficientFundsPayload,
  ConnectionsBillingPreviewPayload,
  ConnectionsBillingPreviewParams,
  SetDefaultMethodParams,
  PaymentMethod
} from '../interfaces/paymentMethods';

import {
  VERIFY_SUFFICIENT_FUNDS_REQUEST,
  VERIFY_SUFFICIENT_FUNDS_SUCCESS,
  VERIFY_SUFFICIENT_FUNDS_FAILURE,
  FETCH_PAYMENT_METHODS_REQUEST,
  FETCH_PAYMENT_METHODS_SUCCESS,
  FETCH_PAYMENT_METHODS_FAILURE,
  FETCH_CONNECTIONS_BILLING_PREVIEW_REQUEST,
  FETCH_CONNECTIONS_BILLING_PREVIEW_SUCCESS,
  FETCH_CONNECTIONS_BILLING_PREVIEW_FAILURE,
  SET_DEFAULT_PAYMENT_METHOD_REQUEST,
  SET_DEFAULT_PAYMENT_METHOD_SUCCESS,
  SET_DEFAULT_PAYMENT_METHOD_FAILURE,
  GET_PAYMENT_METHODS_DETAILS_REQUEST,
  GET_PAYMENT_METHODS_DETAILS_SUCCESS,
  GET_PAYMENT_METHODS_DETAILS_FAILURE,
  RESET_NEW_PAYMENT_METHOD,
  RESET_CURRENT_SELECTED,
  SET_CURRENT_SELECTED
} from '../constants/paymentMethods';
import { BillingErrorType } from '../interfaces';
import { Actions as BillingErrorActions, errorCatch } from './billingErrors';

const verifySufficientFundsRequest = () =>
  ({
    type: VERIFY_SUFFICIENT_FUNDS_REQUEST
  } as const);

const verifySufficientFundsSuccess = (payload: VerifySufficientFundsPayload) =>
  ({
    type: VERIFY_SUFFICIENT_FUNDS_SUCCESS,
    payload
  } as const);

const verifySufficientFundsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: VERIFY_SUFFICIENT_FUNDS_FAILURE,
    error
  } as const;
};

export const verifySufficientFunds = (
  params: VerifySufficientFundsParams
): ThunkAction<void, RootState, null, Actions | BillingErrorActions> => async dispatch => {
  try {
    dispatch(verifySufficientFundsRequest());

    if (params.amount === 0) {
      const payload = { hasSufficientFunds: true, methodId: params.paymentMethodId };
      dispatch(verifySufficientFundsSuccess(payload));
      return payload;
    }

    const { data } = await axios.post('/api/v1/billing/PaymentMethod/Verify', params);
    const payload = { hasSufficientFunds: data.success, methodId: params.paymentMethodId };

    dispatch(verifySufficientFundsSuccess(payload));

    return payload;
  } catch (error) {
    dispatch(errorCatch({ place: 'billing/verifySufficientFunds/rejected', data: BillingErrorType.VerifyFunds }));
    dispatch(verifySufficientFundsFailure(error));

    throw error;
  }
};

const fetchPaymentMethodsRequest = () =>
  ({
    type: FETCH_PAYMENT_METHODS_REQUEST
  } as const);

const fetchPaymentMethodsSuccess = (payload: FetchPaymentMethodsPayload) =>
  ({
    type: FETCH_PAYMENT_METHODS_SUCCESS,
    payload
  } as const);

const fetchPaymentMethodsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_PAYMENT_METHODS_FAILURE,
    error
  } as const;
};

export const fetchPaymentMethods = (
  params: FetchPaymentMethodsParams
): ThunkAction<void, RootState, null, Actions | BillingErrorActions> => async dispatch => {
  try {
    dispatch(fetchPaymentMethodsRequest());

    const { data } = await axios.get<FetchPaymentMethodsPayload>(
      `/api/v1/billing/PaymentMethod/company/${params.companyId}`
    );
    dispatch(fetchPaymentMethodsSuccess(data));
  } catch (error) {
    params.initialLoad &&
      dispatch(
        errorCatch({ place: 'billing/paymentMethods/fetch/rejected', data: BillingErrorType.FetchPaymentMethods })
      );

    dispatch(fetchPaymentMethodsFailure(error));
  }
};

const fetchConnectionsBillingPreviewRequest = () =>
  ({
    type: FETCH_CONNECTIONS_BILLING_PREVIEW_REQUEST
  } as const);

const fetchConnectionsBillingPreviewSuccess = (payload: ConnectionsBillingPreviewPayload) =>
  ({
    type: FETCH_CONNECTIONS_BILLING_PREVIEW_SUCCESS,
    payload
  } as const);

const fetchConnectionsBillingPreviewFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_CONNECTIONS_BILLING_PREVIEW_FAILURE,
    error
  } as const;
};

export const fetchConnectionsBillingPreview = (
  params: ConnectionsBillingPreviewParams
): ThunkAction<void, RootState, null, Actions | BillingErrorActions> => async dispatch => {
  try {
    dispatch(fetchConnectionsBillingPreviewRequest());

    const { data } = await axios.post<ConnectionsBillingPreviewPayload>('/api/v1/billing/Connection/preview', params);

    dispatch(fetchConnectionsBillingPreviewSuccess(data));
    return data;
  } catch (error) {
    dispatch(
      errorCatch({
        place: 'billing/fetchConnectionsBillingPreview/rejected',
        data: BillingErrorType.BillingPreview
      })
    );

    dispatch(fetchConnectionsBillingPreviewFailure(error));
    throw error;
  }
};

const setDefaultPaymentMethodRequest = () =>
  ({
    type: SET_DEFAULT_PAYMENT_METHOD_REQUEST
  } as const);

const setDefaultPaymentMethodSuccess = () =>
  ({
    type: SET_DEFAULT_PAYMENT_METHOD_SUCCESS
  } as const);

const setDefaultPaymentMethodFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: SET_DEFAULT_PAYMENT_METHOD_FAILURE,
    error
  } as const;
};

export const setDefaultPaymentMethod = ({
  companyId,
  paymentMethodId
}: SetDefaultMethodParams): ThunkAction<void, RootState, null, Actions | BillingErrorActions> => async dispatch => {
  try {
    dispatch(setDefaultPaymentMethodRequest());

    const data = await axios.patch(`/api/v1/billing/Company/${companyId}/paymentMethod/${paymentMethodId}`);

    if (data) {
      dispatch(fetchPaymentMethods({ companyId: companyId }));
    }

    dispatch(setDefaultPaymentMethodSuccess());
  } catch (error) {
    dispatch(
      errorCatch({ place: 'billing/setDefaultPaymentMethod/rejected', data: BillingErrorType.SetDefaultMethod })
    );

    dispatch(setDefaultPaymentMethodFailure(error));
  }
};

const getPaymentMethodDetailsRequest = () =>
  ({
    type: GET_PAYMENT_METHODS_DETAILS_REQUEST
  } as const);

const getPaymentMethodDetailsSuccess = (payload: PaymentMethod) =>
  ({
    type: GET_PAYMENT_METHODS_DETAILS_SUCCESS,
    payload
  } as const);

const getPaymentMethodDetailsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: GET_PAYMENT_METHODS_DETAILS_FAILURE,
    error
  } as const;
};

export const getPaymentMethodDetails = (
  id: string
): ThunkAction<void, RootState, null, Actions | BillingErrorActions> => async dispatch => {
  try {
    dispatch(getPaymentMethodDetailsRequest());

    const { data } = await axios.get('/api/v1/billing/PaymentMethod', { params: { id } });

    dispatch(getPaymentMethodDetailsSuccess(data));
  } catch (error) {
    dispatch(errorCatch({ place: 'billing/getPaymentMethodDetails/rejected', data: BillingErrorType.PaymentDetails }));
    dispatch(getPaymentMethodDetailsFailure(error));
  }
};

export const resetNewPaymentMethod = () =>
  ({
    type: RESET_NEW_PAYMENT_METHOD
  } as const);

export const resetCurrentSelected = () =>
  ({
    type: RESET_CURRENT_SELECTED
  } as const);

export const setCurrentSelected = (payload: { id: string; isAuthorized: boolean | null }) =>
  ({
    type: SET_CURRENT_SELECTED,
    payload
  } as const);

export type Actions =
  | ReturnType<typeof verifySufficientFundsRequest>
  | ReturnType<typeof verifySufficientFundsSuccess>
  | ReturnType<typeof verifySufficientFundsFailure>
  | ReturnType<typeof fetchPaymentMethodsRequest>
  | ReturnType<typeof fetchPaymentMethodsSuccess>
  | ReturnType<typeof fetchPaymentMethodsFailure>
  | ReturnType<typeof fetchConnectionsBillingPreviewRequest>
  | ReturnType<typeof fetchConnectionsBillingPreviewSuccess>
  | ReturnType<typeof fetchConnectionsBillingPreviewFailure>
  | ReturnType<typeof setDefaultPaymentMethodRequest>
  | ReturnType<typeof setDefaultPaymentMethodSuccess>
  | ReturnType<typeof setDefaultPaymentMethodFailure>
  | ReturnType<typeof getPaymentMethodDetailsRequest>
  | ReturnType<typeof getPaymentMethodDetailsSuccess>
  | ReturnType<typeof getPaymentMethodDetailsFailure>
  | ReturnType<typeof resetNewPaymentMethod>
  | ReturnType<typeof resetCurrentSelected>
  | ReturnType<typeof setCurrentSelected>;
